import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { CheckCircle2, MessageCircle, Loader2, Brain, Users, HeartHandshake, MonitorSmartphone, MessageSquare, Shield, ArrowRight, X, ChevronRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import DependenciaTestModal from '../components/DependenciaTestModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const WSP_NUMBER = '+56939014212';
const WSP_URL = `https://wa.me/56939014212?text=${encodeURIComponent('Necesito más información sobre tratamiento online')}`;

const TIPOS_CONSUMO = [
  { v: 'alcohol', l: 'Alcohol' },
  { v: 'cocaina', l: 'Cocaína' },
  { v: 'pasta_base', l: 'Pasta Base' },
  { v: 'marihuana', l: 'Marihuana' },
  { v: 'medicamentos', l: 'Medicamentos / Benzodiacepinas' },
  { v: 'ludopatia', l: 'Ludopatía' },
  { v: 'polisustancia', l: 'Múltiples sustancias' },
  { v: 'otro', l: 'Otro' },
];

const FAQ_ITEMS = [
  {
    q: '¿Funciona realmente un tratamiento de adicciones 100% online?',
    a: 'Sí, cuando la evaluación profesional determina que es adecuado. Diversas intervenciones psicoterapéuticas y grupales han demostrado ser eficaces también en modalidad online, sobre todo en consumos leves y moderados y en prevención de recaídas. Antes de ingresar hacemos una evaluación individual para confirmar si tu caso es apto para tratamiento online.'
  },
  {
    q: '¿Qué diferencia hay con otros tratamientos online que solo son videollamadas?',
    a: 'Muchos programas online se limitan a una videollamada semanal. Nuestro programa combina psicólogo especialista, terapeuta en adicciones, terapia grupal, coach de hábitos, acompañamiento por chat 24/7, reunión familiar mensual y una plataforma digital de seguimiento. La recuperación no ocurre solo en la sesión — ocurre entre sesiones.'
  },
  {
    q: '¿Qué pasa si tengo una recaída durante el programa?',
    a: 'La recaída no es fracaso, es información. Tenemos chat 24/7 justamente para esos momentos, un plan de contingencia con tu terapeuta y estrategias grupales de manejo del deseo. Trabajamos con enfoque de prevención de recaídas: qué gatilló, qué aprender, cómo seguir.'
  },
  {
    q: '¿Cómo participa mi familia?',
    a: 'Una vez al mes hacemos una reunión familiar online orientada a entregar herramientas: cómo acompañar sin sobreproteger, establecer límites, comunicación, recuperación de la confianza y comprensión del proceso. No convertimos a la familia en policías — la convertimos en red de apoyo preparada.'
  },
  {
    q: '¿Puedo mantener mi trabajo y estudios mientras estoy en el programa?',
    a: 'Sí. El programa está diseñado justamente para personas que necesitan tratamiento profesional sin abandonar sus responsabilidades. Todas las sesiones se agendan en horarios compatibles con la jornada laboral, y las herramientas digitales están disponibles 24/7.'
  },
  {
    q: '¿Y si al evaluarme me dicen que no soy candidato al programa online?',
    a: 'Te lo diremos con transparencia. Gracias a nuestra red de +220 centros verificados en las 16 regiones de Chile, podemos ayudarte a encontrar un tratamiento residencial, ambulatorio intensivo o el nivel de atención que corresponda. Nuestro compromiso es que encuentres el tratamiento que necesitas, no venderte uno que no necesitas.'
  },
  {
    q: '¿Cuánto dura el programa y cómo se paga?',
    a: 'El valor es $349.990 CLP mensuales por todo el programa completo. La duración recomendada depende de tu evaluación inicial; típicamente 3 a 6 meses, con revisión mensual del plan de recuperación. Puedes cancelar cuando quieras.'
  },
  {
    q: '¿Cuándo comienza la próxima cohorte?',
    a: 'La próxima cohorte comienza el 17 de septiembre de 2026 con cupos limitados. Si te interesa asegurar tu lugar, completa el formulario de evaluación y nuestro equipo te contactará en las próximas horas.'
  }
];

export default function TratamientoOnlinePage() {
  const [cupos, setCupos] = useState({ taken: 0, total: 5, available: 5 });
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0 });

  useEffect(() => {
    axios.get(`${API}/programa-online/cupos`)
      .then((r) => setCupos(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const target = new Date('2026-09-17T09:00:00-04:00').getTime();
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) { setCountdown({ days: 0, hours: 0, minutes: 0 }); return; }
      setCountdown({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
      });
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, []);

  const scrollToForm = () => {
    document.getElementById('form-cupos')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#fbfaf7] text-[#1a2942] font-serif" data-testid="tratamiento-online-page">
      <title>Tratamiento de Adicciones Online en Chile · Programa Integral SinAdicciones.org</title>
      <meta name="description" content="Programa online de tratamiento de adicciones con psicólogo, terapeuta especialista, terapia grupal, seguimiento 24/7 y trabajo familiar. Comienza el 17 de septiembre. Solo 5 cupos. $349.990/mes." />
      <meta name="keywords" content="tratamiento adicciones online chile, rehabilitación online, terapia adicciones online, tratamiento ambulatorio online, ayuda alcoholismo drogas" />
      <link rel="canonical" href="https://sinadicciones.org/tratamiento-adicciones-online" />
      <meta property="og:title" content="Tratamiento de Adicciones Online · SinAdicciones.org" />
      <meta property="og:description" content="Programa integral online: psicólogo + terapeuta + comunidad + familia + seguimiento 24/7. Comienza 17 de septiembre. $349.990/mes." />
      <meta property="og:url" content="https://sinadicciones.org/tratamiento-adicciones-online" />
      <meta property="og:type" content="website" />

      {/* Rich snippet: MedicalTherapy + Offer + Organization */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "MedicalTherapy",
            "name": "Tratamiento Online SinAdicciones",
            "description": "Programa integral online de tratamiento de adicciones: psicólogo especialista, terapeuta en adicciones, terapia grupal semanal, trabajo con la familia, seguimiento 24/7 y plataforma digital.",
            "medicineSystem": "https://schema.org/WesternConventional",
            "recognizingAuthority": {"@type": "Organization", "name": "SinAdicciones.org"},
            "relevantSpecialty": ["Adicciones", "Salud Mental", "Psicología Clínica"],
            "url": "https://sinadicciones.org/tratamiento-adicciones-online"
          },
          {
            "@type": "Offer",
            "name": "Programa mensual Tratamiento Online",
            "price": "349990",
            "priceCurrency": "CLP",
            "availability": "https://schema.org/InStock",
            "validFrom": "2026-09-17",
            "url": "https://sinadicciones.org/tratamiento-adicciones-online",
            "seller": {"@type": "Organization", "name": "SinAdicciones.org", "url": "https://sinadicciones.org"}
          },
          {
            "@type": "FAQPage",
            "mainEntity": FAQ_ITEMS.map(f => ({
              "@type": "Question",
              "name": f.q,
              "acceptedAnswer": {"@type": "Answer", "text": f.a}
            }))
          }
        ]
      })}} />

      <MinimalHeader onCtaClick={scrollToForm} />

      {/* HERO */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#fdf6ec] border border-[#e0cea2] mb-6" data-testid="hero-badge">
            <Sparkles className="w-3.5 h-3.5 text-[#a48347]" />
            <span className="text-xs uppercase tracking-widest text-[#7a5820] font-sans font-semibold">Comienza el 17 de septiembre</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight leading-[1.15] mb-6" data-testid="hero-title">
            Tratamiento de<br />
            <span className="italic text-[#3b6bb1]">adicciones online</span>
          </h1>

          <p className="text-lg md:text-xl text-[#5a6b8c] leading-relaxed max-w-2xl mx-auto mb-4 font-sans">
            Recuperar tu vida requiere más que dejar de consumir.
          </p>
          <p className="text-base md:text-lg text-[#5a6b8c] leading-relaxed max-w-2xl mx-auto mb-10 font-sans">
            Un programa integral para personas que necesitan cambiar su relación con el alcohol y otras drogas, pero pueden continuar viviendo, trabajando y desarrollando sus responsabilidades mientras realizan su proceso.
          </p>

          <div className="inline-block bg-white border border-[#e6e2d5] rounded-2xl px-8 py-6 mb-8 shadow-sm">
            <div className="text-4xl md:text-5xl font-normal text-[#1a2942] mb-1">$349.990</div>
            <div className="text-sm text-[#8896b3] font-sans">CLP mensuales · programa completo</div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <button
              onClick={scrollToForm}
              className="bg-[#1a2942] hover:bg-[#2a3652] text-white px-8 py-4 rounded-full font-sans font-semibold text-sm inline-flex items-center gap-2 transition-colors"
              data-testid="hero-cta-form"
            >
              Solicitar mi cupo <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setTestModalOpen(true)}
              className="bg-white border border-[#1a2942] text-[#1a2942] hover:bg-[#f4f0e6] px-8 py-4 rounded-full font-sans font-semibold text-sm inline-flex items-center gap-2 transition-colors"
              data-testid="hero-cta-test"
            >
              ¿Es para mí? Hacer test <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <p className="text-xs text-[#8896b3] mt-6 font-sans">
            100% online · Atención profesional · Acompañamiento continuo · Solo <strong className="text-[#a48347]">{Math.max(0, cupos.available)} de {cupos.total} cupos</strong>
          </p>

          {/* COUNTDOWN */}
          {(countdown.days > 0 || countdown.hours > 0) && (
            <div className="mt-8 inline-flex items-center gap-4 md:gap-6 bg-white border border-[#e6e2d5] rounded-2xl px-6 py-4 shadow-sm" data-testid="countdown">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-normal text-[#1a2942] tabular-nums">{countdown.days}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#8896b3] font-sans font-semibold">días</div>
              </div>
              <div className="w-px h-8 bg-[#e6e2d5]" />
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-normal text-[#1a2942] tabular-nums">{countdown.hours}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#8896b3] font-sans font-semibold">horas</div>
              </div>
              <div className="w-px h-8 bg-[#e6e2d5]" />
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-normal text-[#1a2942] tabular-nums">{countdown.minutes}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#8896b3] font-sans font-semibold">min</div>
              </div>
              <div className="hidden md:block pl-4 text-left border-l border-[#e6e2d5]">
                <div className="text-xs uppercase tracking-widest text-[#a48347] font-sans font-semibold mb-0.5">Faltan para</div>
                <div className="text-sm text-[#1a2942] font-sans font-semibold">17 sept · 09:00 hrs</div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* NO ESTÁS SOLO */}
      <section id="programa" className="py-20 px-4 bg-white border-y border-[#e6e2d5] scroll-mt-20">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-normal leading-tight mb-8 text-center">
            No queremos que estés solo<br />entre una sesión y la siguiente
          </h2>
          <div className="text-[#2a3652] font-sans text-base md:text-lg leading-[1.8] space-y-4">
            <p>Uno de los problemas de muchos tratamientos online es que todo termina cuando se cierra la videollamada.</p>
            <p><em>Pero la recuperación ocurre durante toda la semana.</em></p>
            <div className="pl-6 border-l-2 border-[#d4a45a] py-2 my-6 text-[#5a6b8c] italic">
              Cuando aparece una discusión.<br />
              Cuando llega la ansiedad.<br />
              Cuando vuelven las ganas de consumir.<br />
              Cuando tienes que tomar una decisión.<br />
              Cuando necesitas reconstruir una rutina.<br />
              Cuando tu familia no sabe cómo ayudarte.
            </div>
            <p>Por eso creamos un programa que combina <strong>tratamiento profesional, acompañamiento, comunidad, familia, tecnología y construcción de nuevos hábitos.</strong></p>
            <p>No buscamos solamente ayudarte a dejar una sustancia. Buscamos ayudarte a comenzar a construir una vida en la que consumir deje de ser la respuesta.</p>
          </div>
        </div>
      </section>

      {/* UN EQUIPO NO UNA SOLA PERSONA */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <p className="uppercase tracking-widest text-xs text-[#a48347] font-sans font-semibold mb-4">Un equipo, no una sola persona</p>
            <h2 className="text-3xl md:text-4xl font-normal leading-tight">
              Porque la adicción no afecta una sola dimensión de tu vida
            </h2>
            <p className="text-[#5a6b8c] font-sans text-base mt-4 leading-relaxed">
              Afecta pensamientos, emociones, relaciones, hábitos, decisiones, rutinas y entorno. Por eso trabajamos desde diferentes áreas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <PilarCard
              icon={<Brain className="w-6 h-6" />}
              title="Psicólogo especialista en adicciones"
              subtitle="1 sesión individual semanal"
              body="El consumo problemático muchas veces convive con ansiedad, impulsividad, dificultades emocionales, conflictos familiares, experiencias del pasado o patrones que necesitan trabajarse profesionalmente. Trabajamos detonantes emocionales, regulación, prevención de recaídas, relaciones, autoestima y estrategias para situaciones de riesgo."
            />
            <PilarCard
              icon={<HeartHandshake className="w-6 h-6" />}
              title="Terapeuta especialista en adicciones"
              subtitle="1 sesión individual semanal · +15 años de experiencia"
              body="Mientras el trabajo psicológico profundiza en procesos emocionales y cognitivos, el terapeuta trabaja de forma muy cercana sobre la recuperación aplicada a la vida cotidiana: qué ocurrió esta semana, dónde apareció el riesgo, qué decisiones estás tomando, qué necesitas cambiar."
            />
            <PilarCard
              icon={<Users className="w-6 h-6" />}
              title="Terapia grupal semanal online"
              subtitle="Comunidad · 1 reunión semanal"
              body="Hay cosas que solamente comprende alguien que también las ha vivido. Una vez por semana participarás en una reunión con otras personas trabajando problemáticas similares. No para comparar historias — para descubrir que no tienes que atravesar este proceso solo."
            />
            <PilarCard
              icon={<Sparkles className="w-6 h-6" />}
              title="Desarrollo personal y hábitos"
              subtitle="Coach especializado · 1 reunión semanal"
              body="Dejar de consumir abre un espacio. ¿Con qué vas a llenarlo? Trabajamos rutinas, disciplina, sueño, ejercicio, organización, propósito y autocuidado. No reemplaza la terapia — la complementa."
            />
            <PilarCard
              icon={<MonitorSmartphone className="w-6 h-6" />}
              title="Plataforma digital de seguimiento"
              subtitle="Guías · Tests · Registro · Hábitos · Familia"
              body="Tu proceso no queda guardado en una videollamada. Guías de recuperación, evaluaciones, registro de sesiones, seguimiento personal de avances y dificultades, construcción de hábitos y participación familiar cuando corresponda."
            />
            <PilarCard
              icon={<MessageSquare className="w-6 h-6" />}
              title="Acompañamiento por chat 24/7"
              subtitle="Canal disponible todos los días"
              body="No queremos que tengas que esperar siete días para hablar de algo que está ocurriendo hoy. Chat disponible para orientación, comunicar una dificultad o informar una situación relevante. No reemplaza servicios de urgencia médica o psiquiátrica."
            />
          </div>

          {/* Familia */}
          <div className="mt-14 bg-[#fdf6ec] border border-[#e0cea2] rounded-2xl p-8 md:p-10">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-11 h-11 bg-[#d4a45a] text-white rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <p className="uppercase tracking-widest text-xs text-[#7a5820] font-sans font-semibold mb-1">Una vez al mes</p>
                <h3 className="text-2xl font-normal">Trabajamos con la familia</h3>
              </div>
            </div>
            <div className="font-sans text-[#2a3652] text-base leading-[1.8] space-y-3">
              <p>Porque la adicción nunca afecta solamente a una persona. Muchas familias llegan agotadas: han intentado ayudar, discutido, controlado, amenazado, perdonado, puesto límites y después los han retirado. Muchas veces simplemente no saben qué hacer.</p>
              <p>Trabajamos cómo acompañar sin sobreproteger, establecimiento de límites, comunicación, recuperación de la confianza, señales de riesgo, acuerdos familiares y comprensión del proceso de recuperación. <strong>No transformamos a la familia en policías — la convertimos en red de apoyo preparada.</strong></p>
            </div>
          </div>
        </div>
      </section>

      {/* TODO INCLUIDO */}
      <section id="incluye" className="py-20 px-4 bg-white border-y border-[#e6e2d5] scroll-mt-20">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-normal leading-tight mb-4">Todo esto está incluido</h2>
          <p className="text-[#5a6b8c] font-sans">Dentro del mismo programa mensual.</p>
        </div>
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
          <IncluidoList
            title="Cada semana"
            items={[
              '1 sesión individual con terapeuta especialista en adicciones',
              '1 terapia grupal online',
              'Espacio de desarrollo personal y construcción de hábitos',
              'Seguimiento y acompañamiento por chat 24/7',
              'Acceso permanente a plataforma digital de seguimiento',
            ]}
          />
          <IncluidoList
            title="Cada mes"
            items={[
              '2 sesiones individuales con psicólogo especialista en adicciones',
              'Reunión familiar online',
              'Evaluación del progreso',
              'Revisión de objetivos y plan de recuperación',
            ]}
          />
        </div>
      </section>

      {/* PARA QUIÉN SÍ / NO */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
          <div className="bg-white border border-[#e6e2d5] rounded-2xl p-8">
            <div className="w-11 h-11 bg-[#d4edda] text-[#2d6a3e] rounded-full flex items-center justify-center mb-5">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <h3 className="text-2xl font-normal mb-4">¿Para quién está pensado?</h3>
            <p className="font-sans text-[#5a6b8c] text-sm mb-4 leading-relaxed">
              Diseñado principalmente para adultos con consumo problemático o trastornos por consumo de intensidad leve o moderada, cuando una evaluación profesional determina que un tratamiento ambulatorio y online es adecuado.
            </p>
            <ul className="space-y-3 font-sans text-[#2a3652] text-sm leading-relaxed">
              {[
                'Han comenzado a perder el control sobre su consumo',
                'Consumen más de lo planificado o les cuesta detenerse',
                'Han intentado disminuir o dejar sin lograrlo',
                'El consumo ya está afectando pareja, familia, trabajo o economía',
                'Necesitan tratamiento sin abandonar trabajo o responsabilidades',
                'Han hecho tratamiento antes y necesitan continuidad',
              ].map((item, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[#a48347] mt-1">→</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-[#e6e2d5] rounded-2xl p-8">
            <div className="w-11 h-11 bg-[#fce4d6] text-[#a13b12] rounded-full flex items-center justify-center mb-5">
              <Shield className="w-5 h-5" />
            </div>
            <h3 className="text-2xl font-normal mb-4">¿Cuándo NO es suficiente?</h3>
            <p className="font-sans text-[#5a6b8c] text-sm mb-4 leading-relaxed">
              Confianza también significa decir cuándo nuestro programa no es el tratamiento adecuado. Una modalidad online puede no bastar cuando existe:
            </p>
            <ul className="space-y-3 font-sans text-[#2a3652] text-sm leading-relaxed">
              {[
                'Riesgo médico asociado a abstinencia',
                'Consumo severo que requiere desintoxicación supervisada',
                'Riesgo importante para la persona o terceros',
                'Síntomas psiquiátricos agudos',
                'Necesidad de supervisión permanente',
                'Indicación profesional de hospitalización o tratamiento residencial',
              ].map((item, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[#a13b12] mt-1">→</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-xs text-[#8896b3] font-sans leading-relaxed">
              Antes de ingresar evaluamos tu situación. Si necesitas un nivel diferente, te lo diremos y gracias a nuestra red podemos ayudarte a buscar alternativas.
            </p>
          </div>
        </div>
      </section>

      {/* NELSON + AUTORIDAD */}
      <section id="nelson" className="py-20 px-4 bg-white border-y border-[#e6e2d5] scroll-mt-20">
        <div className="max-w-3xl mx-auto text-center">
          <p className="uppercase tracking-widest text-xs text-[#a48347] font-sans font-semibold mb-6">Por qué SinAdicciones.org</p>
          <h2 className="text-3xl md:text-4xl font-normal leading-tight mb-8">
            Una plataforma creada también desde la experiencia
          </h2>
          <p className="font-sans text-[#2a3652] text-base leading-[1.8] mb-6">
            SinAdicciones.org se ha convertido en el directorio especializado en tratamiento de adicciones en Chile, con <strong>+220 centros verificados en las 16 regiones</strong>. Conocemos el ecosistema: sabemos que algunas personas necesitan internarse, otras un ambulatorio intensivo, otras profesionales especializados. Otras pueden beneficiarse de un tratamiento estructurado online.
          </p>
          <blockquote className="border-l-4 border-[#d4a45a] pl-6 py-3 my-8 text-left max-w-2xl mx-auto">
            <p className="italic font-serif text-lg md:text-xl text-[#1a2942] leading-relaxed mb-3">
              &ldquo;Aprendí que dejar la droga era solamente una parte del proceso. Después había que aprender a vivir sin necesitar volver a ella.&rdquo;
            </p>
            <footer className="font-sans text-sm text-[#5a6b8c]">
              <strong className="text-[#1a2942]">Nelson González</strong> · Fundador SinAdicciones.org<br />
              Autor de <em>&ldquo;El Principio del Mapa Inverso: Sobriedad con Sentido&rdquo;</em> · Adicto en recuperación
            </footer>
          </blockquote>
        </div>
      </section>

      {/* FORMULARIO */}
      <FormSection cupos={cupos} onSubmitted={() => {
        axios.get(`${API}/programa-online/cupos`).then((r) => setCupos(r.data)).catch(() => {});
      }} />

      {/* CIERRE */}
      <section className="py-20 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-normal leading-tight mb-6">
            No necesitas decidir hoy si puedes cambiar toda tu vida.<br />
            <span className="italic text-[#3b6bb1]">Solo necesitas decidir cuál será tu próximo paso.</span>
          </h2>
          <p className="font-sans text-[#5a6b8c] text-base leading-relaxed mb-8">
            La recuperación no comienza cuando tienes todas las respuestas. Comienza cuando decides buscar ayuda.
          </p>
          <a
            href={WSP_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-[#25d366] hover:bg-[#1eb85a] text-white px-8 py-4 rounded-full font-sans font-semibold text-sm transition-colors"
            data-testid="cierre-wsp"
          >
            <MessageCircle className="w-4 h-4" /> Conversar por WhatsApp
          </a>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-4 bg-white border-y border-[#e6e2d5] scroll-mt-20" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="uppercase tracking-widest text-xs text-[#a48347] font-sans font-semibold mb-4">Preguntas frecuentes</p>
            <h2 className="text-3xl md:text-4xl font-normal leading-tight">Las dudas más comunes</h2>
          </div>
          <div className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <FaqItem key={i} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      </section>

      <MinimalFooter />

      {/* Modal Test Dependencia */}
      <DependenciaTestModal open={testModalOpen} onClose={() => setTestModalOpen(false)} onCtaClick={() => { setTestModalOpen(false); scrollToForm(); }} />

      {/* WhatsApp Floating Button */}
      <a
        href={WSP_URL}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-6 right-6 z-40 bg-[#25d366] hover:bg-[#1eb85a] text-white w-14 h-14 md:w-16 md:h-16 rounded-full shadow-xl flex items-center justify-center transition-transform hover:scale-105"
        aria-label="WhatsApp"
        data-testid="floating-wsp"
      >
        <MessageCircle className="w-6 h-6 md:w-7 md:h-7" />
      </a>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* HEADER / FOOTER minimales                                           */
/* ------------------------------------------------------------------ */
function MinimalHeader({ onCtaClick }) {
  return (
    <header className="fixed top-0 left-0 right-0 z-30 bg-[#fbfaf7]/95 backdrop-blur-md border-b border-[#e6e2d5]" data-testid="minimal-header">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3.5">
        <Link to="/" className="flex items-center gap-2 font-serif text-lg text-[#1a2942]">
          <span className="w-2 h-2 bg-[#3b6bb1] rounded-full" />
          <span>SinAdicciones<span className="text-[#8896b3]">.org</span></span>
        </Link>
        <nav className="hidden md:flex items-center gap-6 font-sans text-sm text-[#5a6b8c]">
          <a href="#programa" onClick={(e) => { e.preventDefault(); document.getElementById('programa')?.scrollIntoView({ behavior: 'smooth' }); }} className="hover:text-[#1a2942]">Programa</a>
          <a href="#incluye" onClick={(e) => { e.preventDefault(); document.getElementById('incluye')?.scrollIntoView({ behavior: 'smooth' }); }} className="hover:text-[#1a2942]">Qué incluye</a>
          <a href="#nelson" onClick={(e) => { e.preventDefault(); document.getElementById('nelson')?.scrollIntoView({ behavior: 'smooth' }); }} className="hover:text-[#1a2942]">Quiénes somos</a>
          <a href="#faq" onClick={(e) => { e.preventDefault(); document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' }); }} className="hover:text-[#1a2942]">FAQ</a>
        </nav>
        <button
          onClick={onCtaClick}
          className="bg-[#1a2942] hover:bg-[#2a3652] text-white px-5 py-2.5 rounded-full font-sans font-semibold text-xs md:text-sm transition-colors"
          data-testid="header-cta"
        >
          Solicitar cupo
        </button>
      </div>
    </header>
  );
}

function MinimalFooter() {
  return (
    <footer className="bg-[#1a2942] text-white/80 py-10 px-4 font-sans">
      <div className="max-w-4xl mx-auto text-center text-sm space-y-3">
        <p className="text-white font-semibold">SinAdicciones.org</p>
        <p>Directorio nacional de tratamiento de adicciones en Chile · +220 centros verificados</p>
        <p className="text-xs text-white/60">
          <Link to="/" className="hover:text-white">Inicio</Link> · <Link to="/blog" className="hover:text-white">Blog</Link> · <Link to="/herramientas" className="hover:text-white">Herramientas gratuitas</Link> · <Link to="/quienes-somos" className="hover:text-white">Quiénes somos</Link>
        </p>
        <p className="text-xs text-white/50 pt-4 border-t border-white/10 mt-6">
          Emergencia 24/7 · Fonodrogas SENDA: <strong className="text-white/80">1412</strong>
        </p>
      </div>
    </footer>
  );
}

/* ------------------------------------------------------------------ */
/* PILAR CARD                                                          */
/* ------------------------------------------------------------------ */
function PilarCard({ icon, title, subtitle, body }) {
  return (
    <div className="bg-white border border-[#e6e2d5] rounded-2xl p-7 hover:shadow-md transition-shadow" data-testid="pilar-card">
      <div className="w-11 h-11 bg-[#f4f0e6] text-[#a48347] rounded-full flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-lg font-normal mb-1 leading-snug">{title}</h3>
      <p className="text-xs uppercase tracking-widest text-[#a48347] font-sans font-semibold mb-3">{subtitle}</p>
      <p className="font-sans text-sm text-[#5a6b8c] leading-relaxed">{body}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* INCLUIDO LIST                                                       */
/* ------------------------------------------------------------------ */
function IncluidoList({ title, items }) {
  return (
    <div>
      <p className="uppercase tracking-widest text-xs text-[#a48347] font-sans font-semibold mb-4">{title}</p>
      <ul className="space-y-3 font-sans">
        {items.map((it, i) => (
          <li key={i} className="flex gap-3 text-[#2a3652] text-sm leading-relaxed">
            <CheckCircle2 className="w-4 h-4 text-[#3b6bb1] mt-1 flex-shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* FAQ ITEM                                                            */
/* ------------------------------------------------------------------ */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#e6e2d5] rounded-xl bg-white overflow-hidden" data-testid="faq-item">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left px-5 py-4 hover:bg-[#fbfaf7] transition-colors font-sans"
        aria-expanded={open}
      >
        <span className="text-[#1a2942] font-semibold text-sm md:text-base pr-4">{q}</span>
        <ChevronRight className={`w-4 h-4 text-[#8896b3] flex-shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 pt-1 font-sans text-sm text-[#5a6b8c] leading-relaxed">
          {a}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* FORM SECTION                                                        */
/* ------------------------------------------------------------------ */
function FormSection({ cupos, onSubmitted }) {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    para_quien: 'yo',
    tipo_consumo: '',
    acuerdo_precio: false,
    situacion: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.acuerdo_precio) {
      toast.error('Necesitas confirmar que estás de acuerdo con el costo mensual');
      return;
    }
    if (!form.tipo_consumo) {
      toast.error('Selecciona el tipo de consumo');
      return;
    }
    if (form.situacion.trim().length < 5) {
      toast.error('Cuéntanos brevemente tu situación');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/programa-online/apply`, form);
      setDone(true);
      onSubmitted?.();
      window.scrollTo({ top: document.getElementById('form-cupos')?.offsetTop || 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error enviando el formulario. Inténtalo nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="form-cupos" className="py-20 px-4 bg-[#fdf6ec] border-y border-[#e0cea2]" data-testid="form-section">
      <div className="max-w-2xl mx-auto">
        {done ? (
          <div className="text-center py-10" data-testid="form-done">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#3b6bb1] text-white rounded-full mb-6">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h2 className="text-3xl md:text-4xl font-normal mb-4">Solicitud recibida</h2>
            <p className="font-sans text-[#2a3652] text-base leading-relaxed max-w-lg mx-auto mb-6">
              Nuestro equipo se pondrá en contacto contigo en las próximas horas para agendar una conversación inicial. Revisa tu email — te acabamos de enviar un resumen del programa.
            </p>
            <a
              href={WSP_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-[#25d366] hover:bg-[#1eb85a] text-white px-6 py-3 rounded-full font-sans font-semibold text-sm"
              data-testid="form-done-wsp"
            >
              <MessageCircle className="w-4 h-4" /> Conversar por WhatsApp ahora
            </a>
          </div>
        ) : (
          <>
            <div className="text-center mb-10">
              <p className="uppercase tracking-widest text-xs text-[#7a5820] font-sans font-semibold mb-3">Comienza el 17 de septiembre · Solo 5 cupos</p>
              <h2 className="text-3xl md:text-4xl font-normal leading-tight mb-3">
                Solicita tu evaluación inicial
              </h2>
              <p className="font-sans text-[#5a6b8c] text-base">
                Evaluaremos tu situación y te orientaremos sobre el nivel de tratamiento más adecuado. Sin compromiso, sin juicios.
              </p>
            </div>

            <form onSubmit={submit} className="bg-white border border-[#e0cea2] rounded-2xl p-6 md:p-8 space-y-6 shadow-sm font-sans" data-testid="cupos-form">

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-[#1a2942] font-semibold text-sm">Tu nombre <span className="text-red-500">*</span></Label>
                  <Input
                    id="name" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ej: María González"
                    className="mt-1 bg-white border-[#d4c9a8] focus:border-[#3b6bb1]"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[#1a2942] font-semibold text-sm">WhatsApp <span className="text-red-500">*</span></Label>
                  <Input
                    id="phone" required value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+56 9 1234 5678"
                    className="mt-1 bg-white border-[#d4c9a8] focus:border-[#3b6bb1]"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-[#1a2942] font-semibold text-sm">Email <span className="text-red-500">*</span></Label>
                <Input
                  id="email" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="tuemail@dominio.cl"
                  className="mt-1 bg-white border-[#d4c9a8] focus:border-[#3b6bb1]"
                  data-testid="input-email"
                />
              </div>

              <div>
                <Label className="text-[#1a2942] font-semibold text-sm block mb-2">¿Para quién es la consulta? <span className="text-red-500">*</span></Label>
                <RadioGroup value={form.para_quien} onValueChange={(v) => setForm({ ...form, para_quien: v })} className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="yo" data-testid="para-yo" /> <span className="text-[#2a3652] text-sm">Para mí</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="familiar" data-testid="para-familiar" /> <span className="text-[#2a3652] text-sm">Para un familiar</span>
                  </label>
                </RadioGroup>
              </div>

              <div>
                <Label className="text-[#1a2942] font-semibold text-sm block mb-2">Tipo de consumo <span className="text-red-500">*</span></Label>
                <Select value={form.tipo_consumo} onValueChange={(v) => setForm({ ...form, tipo_consumo: v })}>
                  <SelectTrigger className="bg-white border-[#d4c9a8]" data-testid="select-tipo">
                    <SelectValue placeholder="Selecciona una opción..." />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_CONSUMO.map((t) => (
                      <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="situacion" className="text-[#1a2942] font-semibold text-sm">Cuéntanos tu situación <span className="text-red-500">*</span></Label>
                <Textarea
                  id="situacion" required value={form.situacion} rows={5}
                  onChange={(e) => setForm({ ...form, situacion: e.target.value })}
                  placeholder="Describe brevemente qué está pasando, cuánto tiempo llevas con esto, si has probado otros tratamientos..."
                  className="mt-1 bg-white border-[#d4c9a8] focus:border-[#3b6bb1]"
                  data-testid="input-situacion"
                />
              </div>

              <div className="bg-[#fdf6ec] border border-[#e0cea2] rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={form.acuerdo_precio}
                    onCheckedChange={(v) => setForm({ ...form, acuerdo_precio: !!v })}
                    className="mt-0.5"
                    data-testid="checkbox-acuerdo"
                  />
                  <span className="text-sm text-[#2a3652] leading-relaxed">
                    Entiendo que el programa tiene un valor mensual de <strong>$349.990 CLP</strong> y estoy de acuerdo con conocer más detalles.
                  </span>
                </label>
              </div>

              <Button
                type="submit" disabled={submitting}
                className="w-full bg-[#1a2942] hover:bg-[#2a3652] text-white py-6 rounded-full text-base font-semibold"
                data-testid="submit-cupos"
              >
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando…</> : 'Solicitar mi cupo →'}
              </Button>

              <p className="text-xs text-center text-[#8896b3]">
                Al enviar aceptas que nuestro equipo te contacte para agendar la evaluación inicial.
              </p>
            </form>
          </>
        )}
      </div>
    </section>
  );
}
